/**
 * 将揽收APP需要的状态和操作集中管理, 方便无网精准控制状态读取和操作
 */
import { create } from 'zustand';
import { PickupAppointmentType } from '../graphql/types/PickupTaskWithAppointmentsType';
import { useEffect, useMemo } from 'react';
import { immer } from 'zustand/middleware/immer';
import { GetPublishedTaskResponse, GET_PUBLISHED_TASK_BY_DRIVER } from '../graphql/queries/getPublishedTaskByDriverAccountId';
import { client } from '../../../graphql/client';
import { 
    UpdatePickupAppointmentInput, 
    UPDATE_PICKUP_APPOINTMENT, 
    UPDATE_COMPLETED_PICKUP_APPOINTMENT,
    UpdatePickupAppointmentResponse, 
    UpdatePickupAppointmentVariables,
    UpdateCompletedPickupAppointmentResponse
} from '../graphql/mutations/updatePickupAppointment';
import { CreateUnloadRecordInput, CREATE_UNLOAD_RECORD, CreateUnloadRecordResponse, CreateUnloadRecordVariables } from '../graphql/mutations/createUnloadRecord';
import { FinishTaskAppointmentInput, FINISH_TASK_APPOINTMENT } from '../graphql/mutations/finishTaskAppointment';
import {
    HANDLE_PICKUP_FAILED,
    HandlePickupFailedInput,
    HandlePickupFailedResponse
} from '../../../graphql/mutations/handlePickupFailed';
import { MARK_ARRIVED_AT_STARTING_POINT, ArrivedAtStartingPoint } from '../../../graphql/mutations/markArrivedAtStartingPoint';
import {
    SUBMIT_PICKUP_ONSITE_FEEDBACK,
    OnsiteFeedbackInput,
    SubmitPickupOnsiteFeedbackResponse
} from '../graphql/mutations/submitPickupOnsiteFeedback';
import { GET_COMPLETED_TASK_BY_DRIVER, GetCompletedTaskResponse } from '../graphql/queries/getCompletedTaskByDriverAccountId';
import dayjs from 'dayjs';

// 轮询间隔：3分钟
const POLLING_INTERVAL = 3 * 60 * 1000;

// 辅助函数：深度比较两个数组是否相等（不依赖顺序）
const arraysEqual = (a: PickupAppointmentType[], b: PickupAppointmentType[]): boolean => {
    if (a.length !== b.length) return false;
    
    // 按 id 排序后比较，避免顺序影响
    const sortedA = [...a].sort((x, y) => x.id.localeCompare(y.id));
    const sortedB = [...b].sort((x, y) => x.id.localeCompare(y.id));
    
    return sortedA.every((itemA, index) => {
        const itemB = sortedB[index];
        if (!itemB) return false;
        
        // 比较关键字段，忽略可能变化的时间戳等
        return itemA.id === itemB.id &&
               itemA.status === itemB.status &&
               itemA.sequence === itemB.sequence &&
               itemA.isFinished === itemB.isFinished;
    });
};

export interface PickupTaskOperations {
    updatePickupAppointment: (input: UpdatePickupAppointmentInput, optimisticSubmission: boolean) => Promise<boolean>;
    updateCompletedPickupAppointment: (input: UpdatePickupAppointmentInput, optimisticSubmission: boolean) => Promise<boolean>;
    createUnloadRecord: (input: CreateUnloadRecordInput, optimisticSubmission: boolean) => Promise<boolean>;
    finishTaskAppointment: (input: FinishTaskAppointmentInput) => Promise<boolean>;
    handlePickupFailed: (input: HandlePickupFailedInput, optimisticSubmission: boolean) => Promise<boolean>;
    markArrivedAtStartingPoint: (input: ArrivedAtStartingPoint, optimisticSubmission: boolean) => Promise<boolean>;
    submitPickupOnsiteFeedback: (input: OnsiteFeedbackInput) => Promise<boolean>;
}

interface PickupStoreImpl {
    pickupTaskAppointmentList: PickupAppointmentType[];
    loading: boolean;
    _isNeedRefresh: boolean;
    _pollingTimer: NodeJS.Timeout | null;
    _pollingEnabled: boolean;
    // 新增completed相关的状态
    completedPickupTaskAppointmentList: PickupAppointmentType[][];
    completedPickupLoading: boolean;
    _completedPickupIsNeedRefresh: boolean;
    setPickupTaskAppointmentList: (pickupTaskAppointmentList: PickupAppointmentType[]) => void;
    fetchPickupTaskAppointmentList: () => Promise<PickupAppointmentType[]>;
    _fetchPickupTaskAppointmentList: () => Promise<PickupAppointmentType[]>;
    refreshPickupTaskAppointmentList: () => Promise<PickupAppointmentType[]>;
    isNeedRefresh: () => boolean;
    setNeedRefresh: (isNeedRefresh: boolean) => void;
    startPolling: () => void;
    stopPolling: () => void;
    updatePickupAppointment: (input: UpdatePickupAppointmentInput, optimisticSubmission: boolean) => Promise<boolean>;
    updateCompletedPickupAppointment: (input: UpdatePickupAppointmentInput, optimisticSubmission: boolean) => Promise<boolean>;
    createUnloadRecord: (input: CreateUnloadRecordInput, optimisticSubmission: boolean) => Promise<boolean>;
    finishTaskAppointment: (input: FinishTaskAppointmentInput) => Promise<boolean>;
    handlePickupFailed: (input: HandlePickupFailedInput, optimisticSubmission: boolean) => Promise<boolean>;
    markArrivedAtStartingPoint: (input: ArrivedAtStartingPoint, optimisticSubmission: boolean) => Promise<boolean>;
    submitPickupOnsiteFeedback: (input: OnsiteFeedbackInput) => Promise<boolean>;
    _updatePickupTaskAppointment: (pickupTaskAppointment: Partial<PickupAppointmentType>) => void;
    _updateCompletedPickupTaskAppointment: (pickupTaskAppointment: Partial<PickupAppointmentType>) => void;
    // 新增completed相关的方法
    setCompletedPickupTaskAppointmentList: (pickupTaskAppointmentList: PickupAppointmentType[][]) => void;
    fetchCompletedPickupTaskAppointmentList: (selectedDate?: string) => Promise<PickupAppointmentType[][]>;
    _fetchCompletedPickupTaskAppointmentList: (selectedDate?: string) => Promise<PickupAppointmentType[][]>;
    refreshCompletedPickupTaskAppointmentList: (selectedDate?: string) => Promise<PickupAppointmentType[][]>;
    isCompletedPickupNeedRefresh: () => boolean;
    setCompletedPickupNeedRefresh: (isNeedRefresh: boolean) => void;
}

const usePickupStore = create<PickupStoreImpl>()(
    immer((set, get) => ({
        pickupTaskAppointmentList: [],
        loading: false,
        _isNeedRefresh: false,
        _pollingTimer: null,
        _pollingEnabled: false,
        // 新增completed相关的状态
        completedPickupTaskAppointmentList: [],
        completedPickupLoading: false,
        _completedPickupIsNeedRefresh: false,
        setNeedRefresh: (isNeedRefresh: boolean) => {
            set({ _isNeedRefresh: isNeedRefresh });
        },
        setPickupTaskAppointmentList: (pickupTaskAppointmentList) => {
            set({ pickupTaskAppointmentList });
        },
        fetchPickupTaskAppointmentList: async () => {
            const state = get();
            if (!state.isNeedRefresh() && !state.loading) {
                return state.pickupTaskAppointmentList;
            }
            // 防止并发请求
            if (state.loading) {
                return state.pickupTaskAppointmentList;
            }
            return get()._fetchPickupTaskAppointmentList();
        },
        _fetchPickupTaskAppointmentList: async () => {
            set({ loading: true });
            try {
                const { data } = await client.query<GetPublishedTaskResponse>({
                    query: GET_PUBLISHED_TASK_BY_DRIVER
                });
                const newPickupTaskAppointmentList = data?.getPublishedTaskByDriver || [];
                const currentList = get().pickupTaskAppointmentList;
                
                // 数据对比逻辑：标记新增数据
                const processedList = newPickupTaskAppointmentList.map(newItem => {
                    // 检查当前缓存中是否存在该数据
                    const existingItem = currentList.find(item => item.id === newItem.id);
                    
                    if (existingItem) {
                        // 已存在的数据，保持原有的 isNew 状态或设为 false
                        return { ...newItem, isNew: existingItem.isNew || false };
                    } else {
                        // 新增的数据，设置 isNew 为 true（但第一次加载时除外）
                        const isFirstLoad = currentList.length === 0;
                        return { ...newItem, isNew: !isFirstLoad };
                    }
                });
                
                // 只有当数据真正发生变化时才更新状态
                const hasChanges = !arraysEqual(currentList, processedList);
                if (hasChanges) {
                    set({ pickupTaskAppointmentList: processedList });
                }
                
                return get().pickupTaskAppointmentList;
            } catch (error) {
                console.error('Error fetching pickup task appointment list:', error);
                return get().pickupTaskAppointmentList;
            } finally {
                set({ loading: false });
                get().setNeedRefresh(false);
            }
        },
        refreshPickupTaskAppointmentList: async () => {
            get().setNeedRefresh(true);
            return get()._fetchPickupTaskAppointmentList();
        },
        // 判断是否需要重新获取预约列表
        isNeedRefresh: () => {
            return get().pickupTaskAppointmentList.length === 0 || get()._isNeedRefresh;
        },
        // 开始轮询
        startPolling: () => {
            const state = get();
            if (state._pollingTimer || state._pollingEnabled) {
                return; // 已经在轮询中
            }
            
            set({ _pollingEnabled: true });
            
            const poll = async () => {
                if (!get()._pollingEnabled) {
                    return;
                }
                
                try {
                    console.debug('Polling pickup task appointment list...');
                    await get().refreshPickupTaskAppointmentList();
                } catch (error) {
                    console.error('Polling error:', error);
                }
                
                // 设置下一次轮询
                if (get()._pollingEnabled) {
                    const timer = setTimeout(poll, POLLING_INTERVAL);
                    set({ _pollingTimer: timer });
                }
            };
            
            // 立即开始第一次轮询
            const timer = setTimeout(poll, POLLING_INTERVAL);
            set({ _pollingTimer: timer });
        },
        // 停止轮询
        stopPolling: () => {
            const state = get();
            if (state._pollingTimer) {
                clearTimeout(state._pollingTimer);
            }
            set({ 
                _pollingTimer: null,
                _pollingEnabled: false 
            });
        },
        // 新增completed相关的方法实现
        setCompletedPickupTaskAppointmentList: (pickupTaskAppointmentList) => {
            set({ completedPickupTaskAppointmentList: pickupTaskAppointmentList });
        },
        setCompletedPickupNeedRefresh: (isNeedRefresh: boolean) => {
            set({ _completedPickupIsNeedRefresh: isNeedRefresh });
        },
        fetchCompletedPickupTaskAppointmentList: async (selectedDate?: string) => {
            if (!get().isCompletedPickupNeedRefresh()) {
                return get().completedPickupTaskAppointmentList;
            }
            return get()._fetchCompletedPickupTaskAppointmentList(selectedDate);
        },
        _fetchCompletedPickupTaskAppointmentList: async (selectedDate?: string) => {
            set({ completedPickupLoading: true });
            try {
                const { data } = await client.query<GetCompletedTaskResponse>({
                    query: GET_COMPLETED_TASK_BY_DRIVER,
                    variables: {
                        selectedDate: selectedDate || dayjs().format('YYYY-MM-DD').toString()
                    },
                    fetchPolicy: 'network-only', // 确保获取最新数据
                });
                const completedPickupTaskAppointmentList = data?.getTaskByDriverAccountId || [];
                set({ completedPickupTaskAppointmentList });
                return get().completedPickupTaskAppointmentList;
            } catch (error) {
                console.error('Error fetching completed pickup task appointment list:', error);
                return get().completedPickupTaskAppointmentList;
            } finally {
                set({ completedPickupLoading: false });
                get().setCompletedPickupNeedRefresh(false);
            }
        },
        refreshCompletedPickupTaskAppointmentList: async (selectedDate?: string) => {
            get().setCompletedPickupNeedRefresh(true);
            return get()._fetchCompletedPickupTaskAppointmentList(selectedDate);
        },
        isCompletedPickupNeedRefresh: () => {
            return get().completedPickupTaskAppointmentList.length === 0 || get()._completedPickupIsNeedRefresh;
        },
        _updatePickupTaskAppointment: (partialPickupTaskAppointment: Partial<PickupAppointmentType>) => {
            set(state => {
                const index = state.pickupTaskAppointmentList.findIndex(item => item.id === partialPickupTaskAppointment.id);
                if (index !== -1) {
                    const oldPickupTaskAppointment = state.pickupTaskAppointmentList[index];
                    const newPickupTaskAppointment = { ...oldPickupTaskAppointment, ...partialPickupTaskAppointment };
                    state.pickupTaskAppointmentList[index] = newPickupTaskAppointment;
                } else if (partialPickupTaskAppointment.id) {
                    state.pickupTaskAppointmentList.push(partialPickupTaskAppointment as PickupAppointmentType);
                }
            });
        },
        _updateCompletedPickupTaskAppointment: (partialPickupTaskAppointment: Partial<PickupAppointmentType>) => {
            set(state => {
                // 在二维数组中查找并更新指定的预约单
                for (let taskIndex = 0; taskIndex < state.completedPickupTaskAppointmentList.length; taskIndex++) {
                    const taskAppointments = state.completedPickupTaskAppointmentList[taskIndex];
                    const appointmentIndex = taskAppointments.findIndex(item => {
                        // 同时匹配appointmentId和taskId确保唯一性
                        return item.id === partialPickupTaskAppointment.id && item.taskId === partialPickupTaskAppointment.taskId;
                    });
                    if (appointmentIndex !== -1) {
                        const oldPickupTaskAppointment = taskAppointments[appointmentIndex];
                        const newPickupTaskAppointment = { ...oldPickupTaskAppointment, ...partialPickupTaskAppointment };
                        state.completedPickupTaskAppointmentList[taskIndex][appointmentIndex] = newPickupTaskAppointment;
                        return;
                    }
                }
            });
        },
        updatePickupAppointment: async (input: UpdatePickupAppointmentInput, optimisticSubmission: boolean) => {
            const setPickupAppointmentToUpdated = () => {
                const appointmentState: Partial<PickupAppointmentType> = {
                    id: input.appointmentId,
                    actualQuantity: input.actualParcelCount,
                    actualUnit: input.actualParcelUnit.toString(),
                    actualTime: new Date().toISOString(),
                    status: 'PICKED_UP', // 假设完成状态
                    isFinished: true, // 更新预约时标记为已完成
                    isAbnormal: false,
                };
                get()._updatePickupTaskAppointment(appointmentState);
            };
            if (optimisticSubmission === true){
                setPickupAppointmentToUpdated();
                return true;
            }else {
                try {
                    console.debug('updatePickupAppointment begin:', input);
                    const result = await client.mutate<UpdatePickupAppointmentResponse, UpdatePickupAppointmentVariables>({
                        mutation: UPDATE_PICKUP_APPOINTMENT,
                        variables: { input }
                    });
                    console.debug('updatePickupAppointment result:', result);
                    const success = result.data?.updatePickupAppointment || false;
                    if (success) {
                        setPickupAppointmentToUpdated();
                    }
                    return success;
                } catch (error: any) {
                    console.error('updatePickupAppointment catch error:', error);
                    if (error.networkError) {
                        // 乐观提交：网络错误，更新预约状态
                        console.debug('updatePickupAppointment networkError', error);
                        setPickupAppointmentToUpdated();
                    }
                    throw error;
                }
            }
        },
        updateCompletedPickupAppointment: async (input: UpdatePickupAppointmentInput, optimisticSubmission: boolean) => {
            const setPickupAppointmentToUpdated = () => {
                const appointmentState: Partial<PickupAppointmentType> = {
                    id: input.appointmentId,
                    actualQuantity: input.actualParcelCount,
                    actualUnit: input.actualParcelUnit.toString(),
                    actualTime: new Date().toISOString(),
                    isFinished: true,
                    isAbnormal: false,
                };
                get()._updateCompletedPickupTaskAppointment(appointmentState);
            };
            if (optimisticSubmission === true) {
                setPickupAppointmentToUpdated();
                return true;
            } else {
                try {
                    console.debug('updateCompletedPickupAppointment begin:', input);
                    const result = await client.mutate<UpdateCompletedPickupAppointmentResponse, UpdatePickupAppointmentVariables>({
                        mutation: UPDATE_COMPLETED_PICKUP_APPOINTMENT,
                        variables: { input }
                    });
                    console.debug('updateCompletedPickupAppointment result:', result);
                    const success = result.data?.updateCompletedPickupAppointment || false;
                    if (success) {
                        setPickupAppointmentToUpdated();
                    }
                    return success;
                } catch (error: any) {
                    console.error('updateCompletedPickupAppointment catch error:', error);
                    if (error.networkError) {
                        // 乐观提交：网络错误，更新预约状态
                        console.debug('updateCompletedPickupAppointment networkError', error);
                        setPickupAppointmentToUpdated();
                    }
                    throw error;
                }
            }
        },
        createUnloadRecord: async (input: CreateUnloadRecordInput, optimisticSubmission: boolean) => {
            const setPickupAppointmentToCreateUnloadRecord = () => {
                const appointmentState: Partial<PickupAppointmentType> = {
                    id: '-2',
                    isFinished: input.status === 2,
                };
                get()._updatePickupTaskAppointment(appointmentState);
            };
            if (optimisticSubmission === true){
                setPickupAppointmentToCreateUnloadRecord();
                return true;
            }else {
                try {
                    console.debug('createUnloadRecord begin:', input);
                    const result = await client.mutate<CreateUnloadRecordResponse, CreateUnloadRecordVariables>({
                        mutation: CREATE_UNLOAD_RECORD,
                        variables: { input }
                    });
                    console.debug('createUnloadRecord result:', result);
                    const success = result.data?.createUnloadRecord || false;
                    if (success) {
                        if (input.status === 2){
                            setPickupAppointmentToCreateUnloadRecord();
                        }else {
                            get().setNeedRefresh(true);
                        }
                    }
                    return success;
                } catch (error: any) {
                    console.error('createUnloadRecord catch error:', error);
                    if (error.networkError) {
                        // 乐观提交：网络错误，假设成功
                        console.debug('createUnloadRecord networkError', error);
                        if (input.status === 2){
                            setPickupAppointmentToCreateUnloadRecord();
                        }else {
                            get().setNeedRefresh(true);
                        }
                    }
                    throw error;
                }
            }
        },
        finishTaskAppointment: async (input: FinishTaskAppointmentInput) => {
            try {
                console.debug('finishTaskAppointment begin:', input);
                const result = await client.mutate({
                    mutation: FINISH_TASK_APPOINTMENT,
                    variables: { input }
                });
                console.debug('finishTaskAppointment result:', result);
                const success = result.data?.finishTaskAppointment || false;
                if (success) {
                    // 更新对应预约的状态
                    const appointmentState: Partial<PickupAppointmentType> = {
                        id: input.appointmentId,
                        status: 'FINISHED',
                        isFinished: true,
                    };
                    get()._updatePickupTaskAppointment(appointmentState);
                }
                return success;
            } catch (error: any) {
                console.error('finishTaskAppointment catch error:', error);
                if (error.networkError) {
                    // 乐观提交：网络错误，假设成功
                    console.debug('finishTaskAppointment networkError', error);
                    const appointmentState: Partial<PickupAppointmentType> = {
                        id: input.appointmentId,
                        status: 'FINISHED',
                        isFinished: true,
                    };
                    get()._updatePickupTaskAppointment(appointmentState);
                    return true;
                }
                throw error;
            }
        },
        handlePickupFailed: async (input: HandlePickupFailedInput, optimisticSubmission: boolean) => {
            const setPickupFailed = () => {
                const appointmentState: Partial<PickupAppointmentType> = {
                    id: input.appointmentId,
                    isAbnormal: true, // 标记异常状态
                    isFinished: true, // 更新标记为已完成
                };
                get()._updatePickupTaskAppointment(appointmentState);
            };
            if (optimisticSubmission === true){
                setPickupFailed();
                return true;
            }else {
                try {
                    console.debug('handlePickupFailed begin:', { input});
                    const result = await client.mutate<HandlePickupFailedResponse>({
                        mutation: HANDLE_PICKUP_FAILED,
                        variables: { input}
                    });
                    console.debug('handlePickupFailed result:', result);
                    const success = !!result.data?.handlePickupFailed;
                    if (success) {
                        get().setNeedRefresh(true);
                    }
                    return success;
                } catch (error: any) {
                    console.error('handlePickupFailed catch error:', error);
                    if (error.networkError) {
                        // 乐观提交：网络错误，假设成功
                        console.debug('handlePickupFailed networkError', error);
                        setPickupFailed();
                        get().setNeedRefresh(true);
                        return true;
                    }
                    throw error;
                }
            }
        },
        markArrivedAtStartingPoint: async (input: ArrivedAtStartingPoint, optimisticSubmission: boolean) => {
            const setArrivedAtStartingPoint = () => {
                const appointmentState: Partial<PickupAppointmentType> = {
                    id: input.appointmentId,
                    status: 'PICKING_UP', // 修改任务状态
                    isFinished: true
                };
                get()._updatePickupTaskAppointment(appointmentState);
            };
                if (optimisticSubmission === true){
                    setArrivedAtStartingPoint()
                    return true;
                } else {
                    try {
                        console.debug('markArrivedAtStartingPoint begin:', input);
                        const result = await client.mutate({
                            mutation: MARK_ARRIVED_AT_STARTING_POINT,
                            variables: {
                                input: {
                                    taskId: input.taskId,
                                    appointmentId: input.appointmentId
                                }
                            }
                        });
                        console.debug('markArrivedAtStartingPoint result:', result);
                        const success = !!result.data?.markArrivedAtStartingPoint;
                        if (success) {
                            setArrivedAtStartingPoint()
                        }
                        return success;
                    } catch (error: any) {
                        console.error('markArrivedAtStartingPoint catch error:', error);
                        if (error.networkError) {
                            // 乐观提交：网络错误，假设成功
                            console.debug('markArrivedAtStartingPoint networkError', error);
                            setArrivedAtStartingPoint();
                        }
                        throw error;
                    }
                }
        },
        submitPickupOnsiteFeedback: async (input: OnsiteFeedbackInput) => {
            // 灰度 hub 追加回传：补现场联系人 + 推站点建联状态。best-effort——揽收结果已由 1.0 通路落库，
            // 这里失败不回滚揽收，只上抛由调用方 toast 提示重试（避免因联系人写失败阻断司机主流程）。
            console.debug('submitPickupOnsiteFeedback begin:', input);
            const result = await client.mutate<SubmitPickupOnsiteFeedbackResponse>({
                mutation: SUBMIT_PICKUP_ONSITE_FEEDBACK,
                variables: { input }
            });
            console.debug('submitPickupOnsiteFeedback result:', result);
            return !!result.data?.submitPickupOnsiteFeedback;
        },
    }))
);

export const usePickupTaskAppointmentList = () => {
    const { pickupTaskAppointmentList, fetchPickupTaskAppointmentList, refreshPickupTaskAppointmentList, loading, startPolling, stopPolling } = usePickupStore(state => ({
        pickupTaskAppointmentList: state.pickupTaskAppointmentList,
        fetchPickupTaskAppointmentList: state.fetchPickupTaskAppointmentList,
        refreshPickupTaskAppointmentList: state.refreshPickupTaskAppointmentList,
        loading: state.loading,
        startPolling: state.startPolling,
        stopPolling: state.stopPolling,
    }));
    
    useEffect(() => {
        // 初始化时获取数据并开始轮询
        fetchPickupTaskAppointmentList();
        startPolling();
        
        // 组件卸载时停止轮询
        return () => {
            stopPolling();
        };
    }, []);
    
    return { pickupTaskAppointmentList, loading, refreshPickupTaskAppointmentList };
};

export const usePickupTaskAppointment = (appointmentId: string) => {
    const { pickupTaskAppointmentList, refreshPickupTaskAppointmentList, loading} = usePickupStore(state => ({
        pickupTaskAppointmentList: state.pickupTaskAppointmentList,
        refreshPickupTaskAppointmentList: state.refreshPickupTaskAppointmentList,
        loading: state.loading
    }));
    return {
        pickupTaskAppointment: pickupTaskAppointmentList.find(item => item.id === appointmentId),
        loading,
        refreshPickupTaskAppointmentList
    };
};

export const usePickupTaskOperations = (): PickupTaskOperations => {
    const { updatePickupAppointment, updateCompletedPickupAppointment, createUnloadRecord, finishTaskAppointment, handlePickupFailed, markArrivedAtStartingPoint, submitPickupOnsiteFeedback } = usePickupStore(state => ({
        updatePickupAppointment: state.updatePickupAppointment,
        updateCompletedPickupAppointment: state.updateCompletedPickupAppointment,
        createUnloadRecord: state.createUnloadRecord,
        finishTaskAppointment: state.finishTaskAppointment,
        handlePickupFailed: state.handlePickupFailed,
        markArrivedAtStartingPoint: state.markArrivedAtStartingPoint,
        submitPickupOnsiteFeedback: state.submitPickupOnsiteFeedback,
    }));
    return { updatePickupAppointment, updateCompletedPickupAppointment, createUnloadRecord, finishTaskAppointment, handlePickupFailed, markArrivedAtStartingPoint, submitPickupOnsiteFeedback };
};

export const preload = async () => {
    // 如果需要预加载数据，可以在这里实现
    // 目前 pickup 没有类似 delivery 的异常字典需要预加载
    console.debug('pickup store preload completed');
};

// 导出store实例，用于在非React组件中访问
export { usePickupStore }; 

// 新增completed相关的hooks
export const useCompletedPickupTaskAppointmentList = (selectedDate?: string) => {
    const { 
        completedPickupTaskAppointmentList, 
        fetchCompletedPickupTaskAppointmentList, 
        refreshCompletedPickupTaskAppointmentList, 
        completedPickupLoading,
    } = usePickupStore(state => ({
        completedPickupTaskAppointmentList: state.completedPickupTaskAppointmentList,
        fetchCompletedPickupTaskAppointmentList: state.fetchCompletedPickupTaskAppointmentList,
        refreshCompletedPickupTaskAppointmentList: state.refreshCompletedPickupTaskAppointmentList,
        completedPickupLoading: state.completedPickupLoading,
    }));

    useEffect(() => {
        // 初始化时获取数据，使用传入的selectedDate或当前日期
        const dateToUse = selectedDate || dayjs().format('YYYY-MM-DD').toString();
        fetchCompletedPickupTaskAppointmentList(dateToUse);
    }, [selectedDate]);

    return { 
        pickupTaskAppointmentList: completedPickupTaskAppointmentList, 
        loading: completedPickupLoading, 
        refreshPickupTaskAppointmentList: refreshCompletedPickupTaskAppointmentList 
    };
};

export const useCompletedPickupTaskAppointment = (appointmentId: string, taskId: string) => {
    const { 
        completedPickupTaskAppointmentList, 
        completedPickupLoading
    } = usePickupStore(state => ({
        completedPickupTaskAppointmentList: state.completedPickupTaskAppointmentList,
        completedPickupLoading: state.completedPickupLoading
    }));

    // 在二维数组中查找指定的预约单，同时使用taskId和appointmentId确保唯一性
    const pickupTaskAppointment = useMemo(() => {
        for (const taskAppointments of completedPickupTaskAppointmentList) {
            const appointment = taskAppointments.find(item => {
                // 同时匹配appointmentId和taskId确保唯一性
                return item.id === appointmentId && item.taskId === taskId;
            });
            if (appointment) {
                return appointment;
            }
        }
        return undefined;
    }, [completedPickupTaskAppointmentList, appointmentId, taskId]);

    return {
        pickupTaskAppointment,
        loading: completedPickupLoading,
    };
}; 